import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import * as moment from "moment";

import {
  FilterDateRange,
  FilterLabel,
  FilterValueSelect,
} from "Components/Common/TableFilters";
import { normalize } from "common/lib/normalize";
import { getAgencies } from "common/services/agencies.service";
import { getPageTypes } from "common/services/analytics.service";
import { getUsers } from "common/services/users.service";

import { ReportFilterValue } from "./types";

export const PERIODS = [
  { key: "pastweek", label: "Last 7 days" },
  { key: "pastmonth", label: "Last 30 days" },
  { key: "past6months", label: "Last 6 months" },
  { key: "pastyear", label: "Last year" },
  { key: "all", label: "All time" },
];

/**
 * The agency filter carries the RMU choice rather than a control of its own.
 * The two "all agencies" entries sit above the real agencies; picking RMU from
 * the list below them gives the RMU's usage on its own, which is the third
 * case, so no extra filter is needed to express any of them.
 */
export const AGENCY_ALL = "all";
export const AGENCY_ALL_EXCEPT_RMU = "all-except-rmu";

/** True for the two "all agencies" entries, which are not real agency ids. */
export const isAllAgencies = (agency: string) =>
  !agency || agency === AGENCY_ALL || agency === AGENCY_ALL_EXCEPT_RMU;

export const DEFAULT_FILTERS: ReportFilterValue = {
  period: "pastmonth",
  fromDate: null,
  toDate: null,
  // Opens with the RMU left out. The RMU's own traffic is the great majority
  // of what is recorded, so including it by default answers a question nobody
  // is asking — the point of the report is how the agencies use CIMS. Their
  // usage is one selection away.
  agency: AGENCY_ALL_EXCEPT_RMU,
  page: "",
  user: "",
};

/**
 * The only control surface on the report. Every widget below reads from the
 * same fetch, so whatever is set here changes the entire page at once.
 */
const ReportFilters = ({
  value,
  onChange,
  meta,
  loading,
}: {
  value: ReportFilterValue;
  onChange: (value: ReportFilterValue) => void;
  meta: { startDate: string | null; endDate: string | null } | null;
  loading: boolean;
}) => {
  const [agencies, setAgencies] = useState<any[]>([]);
  const [pages, setPages] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        setLoadingAgencies(true);
        const response: any = await getAgencies({
          pagination: { page: 1, pageSize: 10000 },
          // Same rule the rest of the app uses for "a real agency": the
          // category placeholder rows never own users.
          filters: { category: { $null: false } },
          sort: "name:asc",
        });
        setAgencies(normalize(response.data));
      } catch (e) {
        setAgencies([]);
      } finally {
        setLoadingAgencies(false);
      }
    };

    const fetchPages = async () => {
      try {
        setLoadingPages(true);
        const response: any = await getPageTypes({
          pagination: { page: 1, pageSize: 10000 },
          sort: "name:asc",
        });
        setPages(normalize(response.data));
      } catch (e) {
        setPages([]);
      } finally {
        setLoadingPages(false);
      }
    };

    fetchAgencies();
    fetchPages();
  }, []);

  // The user list follows the agency filter, so picking an agency first keeps
  // the dropdown down to the people who could actually appear in the report.
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const response: any = await getUsers({
          pagination: { page: 1, pageSize: 10000 },
          sort: "firstName:asc",
          populate: ["agency"],
          // The two "all agencies" entries are not agency ids, so they narrow
          // nothing here — the full user list stands.
          ...(isAllAgencies(value.agency)
            ? {}
            : { filters: { agency: { id: { $eq: value.agency } } } }),
        });
        setUsers(normalize(response.data));
      } catch (e) {
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [value.agency]);

  const agencyOptions = useMemo(
    () => [
      { value: AGENCY_ALL, label: "All agencies (including RMU)" },
      { value: AGENCY_ALL_EXCEPT_RMU, label: "All agencies (excluding RMU)" },
      ...agencies.map((el: any) => ({ value: el?.id, label: el?.name })),
    ],
    [agencies]
  );

  const pageOptions = useMemo(
    () => pages.map((el: any) => ({ value: el?.id, label: el?.name })),
    [pages]
  );

  const userOptions = useMemo(
    () =>
      users.map((el: any) => ({
        value: el?.id,
        label:
          [el?.firstName, el?.lastName].filter(Boolean).join(" ") ||
          el?.username ||
          el?.email,
      })),
    [users]
  );

  // Each filter clears itself in place — the date range and the three
  // dropdowns all carry their own clear control — so there is no separate
  // reset, and dropping one choice never discards the rest.
  const usingCustomRange = Boolean(value.fromDate && value.toDate);

  // The window boundaries come back as instants sitting on the reader's own
  // day edges, so they are formatted on the reader's clock — anything else
  // would print a range that disagrees with the dates they picked.
  const rangeCaption = meta?.startDate
    ? `${moment(meta.startDate).format("DD MMM YYYY")} — ${moment(
        meta.endDate || meta.startDate
      ).format("DD MMM YYYY")}`
    : "All recorded activity";


  return (
    <Card
      className="mb-4"
      style={{ position: "sticky", top: "70px", zIndex: 5 }}
    >
      <CardBody>
        <div className="d-flex align-items-start flex-wrap gap-2 mb-3">
          <div className="flex-grow-1">
            <h5 className="card-title mb-1">Activity Report</h5>
            <p className="text-muted mb-0 fs-13">
              {loading ? "Updating…" : rangeCaption}
              {usingCustomRange ? " · custom range" : ""}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-1">
            {PERIODS.map((period) => (
              <button
                key={period.key}
                type="button"
                className={`btn btn-sm material-shadow-none ${
                  !usingCustomRange && value.period === period.key
                    ? "btn-soft-primary"
                    : "btn-soft-secondary"
                }`}
                onClick={() =>
                  onChange({
                    ...value,
                    period: period.key,
                    // A preset replaces any custom range that was in force.
                    fromDate: null,
                    toDate: null,
                  })
                }
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        <Row className="g-3 align-items-end">
          <Col xxl={3} lg={6}>
            <FilterDateRange
              id="activity-report-date-range"
              label="Custom Date Range"
              value={{ fromDate: value.fromDate, toDate: value.toDate }}
              onChange={(dateRange: any) =>
                onChange({
                  ...value,
                  fromDate: dateRange?.fromDate || null,
                  toDate: dateRange?.toDate || null,
                })
              }
            />
          </Col>
          <Col xxl={3} lg={6}>
            <FilterLabel htmlFor="activityReportAgencyFilter">
              Agency
            </FilterLabel>
            <FilterValueSelect
              id="activityReportAgencyFilter"
              placeholder="All agencies"
              value={value.agency}
              // A different agency invalidates whichever user was picked.
              onChange={(agency: any) =>
                onChange({ ...value, agency, user: "" })
              }
              options={agencyOptions}
              isLoading={loadingAgencies}
            />
          </Col>
          <Col xxl={3} lg={6}>
            <FilterLabel htmlFor="activityReportPageFilter">Page</FilterLabel>
            <FilterValueSelect
              id="activityReportPageFilter"
              placeholder="All pages"
              value={value.page}
              onChange={(page: any) => onChange({ ...value, page })}
              options={pageOptions}
              isLoading={loadingPages}
            />
          </Col>
          <Col xxl={3} lg={6}>
            <FilterLabel htmlFor="activityReportUserFilter">User</FilterLabel>
            <FilterValueSelect
              id="activityReportUserFilter"
              placeholder="All users"
              value={value.user}
              onChange={(user: any) => onChange({ ...value, user })}
              options={userOptions}
              isLoading={loadingUsers}
            />
          </Col>
        </Row>

      </CardBody>
    </Card>
  );
};

export default ReportFilters;
